/**
 * Утилита для сохранения и восстановления фильтров в localStorage
 * с проверкой времени (24 часа)
 */

const STORAGE_EXPIRY_HOURS = 24

interface FilterStorageData<T> {
  data: T
  timestamp: number
}

/**
 * Сохраняет фильтры в localStorage с временной меткой
 */
export function saveFilters<T>(key: string, filters: T): void {
  if (!key) return
  
  try {
    const storageData: FilterStorageData<T> = {
      data: filters,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(storageData))
  } catch (err) {
    console.warn(`Не удалось сохранить фильтры для ключа ${key}:`, err)
  }
}

/**
 * Восстанавливает фильтры из localStorage с проверкой времени
 * Возвращает null, если фильтры истекли или не найдены
 */
export function restoreFilters<T>(key: string): T | null {
  if (!key) return null
  
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    
    const parsed: FilterStorageData<T> = JSON.parse(raw)
    
    // Проверяем, не истекли ли фильтры (24 часа)
    const now = Date.now()
    const expiryTime = STORAGE_EXPIRY_HOURS * 60 * 60 * 1000 // 24 часа в миллисекундах
    
    if (now - parsed.timestamp > expiryTime) {
      // Фильтры истекли, удаляем их
      localStorage.removeItem(key)
      return null
    }
    
    return parsed.data
  } catch (err) {
    console.warn(`Не удалось восстановить фильтры для ключа ${key}:`, err)
    // При ошибке парсинга удаляем поврежденные данные
    try {
      localStorage.removeItem(key)
    } catch {
      // Игнорируем ошибку удаления
    }
    return null
  }
}

/**
 * Очищает фильтры для указанного ключа
 */
export function clearFilters(key: string): void {
  if (!key) return
  
  try {
    localStorage.removeItem(key)
  } catch (err) {
    console.warn(`Не удалось очистить фильтры для ключа ${key}:`, err)
  }
}

/**
 * Очищает все фильтры (для использования при выходе)
 */
export function clearAllFilters(): void {
  try {
    const keys = Object.keys(localStorage)
    const filterKeys = keys.filter(key => 
      key.includes('_filters_') || 
      key.includes('product_filters_') ||
      key.includes('repricer_filters_') ||
      key.includes('chestny_znak_filters_')
    )
    
    filterKeys.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch {
        // Игнорируем ошибки удаления отдельных ключей
      }
    })
  } catch (err) {
    console.warn('Не удалось очистить все фильтры:', err)
  }
}

